import type { WatchSource } from 'vue'
import { ResponseError } from '@d/sdk/autogenerated'
import { EasyEventEmitter } from '@d/util'

const CACHE_LOG = true
const CACHE_TIMEOUT = 1000 * 60 * 4
const TOO_MANY_REQUEST_TIMEOUT = 1000 * 10

async function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * CacheGroup is a group of CacheEntries that can be cleared all at once
 */
export class CacheGroup {
  public entries: CacheEntry<any>[] = []

  /**
   * Create a new CacheGroup
   * @param hook Optional hook to clear the cache group when the hook is called (this usually should be a `onBeforeUnmount` hook)
   */
  constructor(hook?: (callback: () => void) => void) {
    if (hook) {
      hook(() => {
        this.clear()
      })
    }
  }

  add(entry: CacheEntry<any>): void {
    this.entries.push(entry)
  }

  clear(): void {
    this.entries.forEach((entry) => {
      entry.release()
    })
    this.entries = []
  }

  invalidate(): void {
    this.entries.forEach((entry) => {
      nextTick(() => {
        entry.invalidate()
      })
    })
  }
}

const CACHE_ENTRY = Symbol('CacheEntry')

export function isCacheEntry(obj: any): obj is CacheEntry<any> {
  return obj[CACHE_ENTRY] ?? false
}

/**
 * CacheEntry is a single entry in the cache that can be acquired and released
 */
export class CacheEntry<T> {
  [CACHE_ENTRY] = true

  constructor(
    public cacheHandler: CacheHandler<T>,
    public key: string,
    public lazyUpdate = false,
  ) {
    this.key = this.cacheHandler.options.keyProcessor?.(this.key) ?? this.key
    this.cacheHandler.acquire(key)
  }

  released = false
  _lastValue: T | null = null

  /**
   * Release the cache entry
   * Usually you don't need to call this manually as long as you've passed a CacheGroup to the CacheEntry
   */
  release(): void {
    if (this.released)
      return
    this.released = true
    this.cacheHandler.release(this.key)
  }

  invalidate(): void {
    if (this.cacheHandler.cacheResolved.has(this.key)) {
      if (this.cacheHandler.cacheResolved.get(this.key) !== null) {
        this._lastValue = this.cacheHandler.cacheResolved.get(this.key) as T
      }
    }
    this.cacheHandler.invalidate(this.key)
  }

  /**
   * Get the loading state of the cache entry
   */
  get loading(): boolean {
    return this.cacheHandler.isLoading(this.key)
  }

  /**
   * If lazyUpdate is enabled, this will return true if the cache entry is currently loading and the last value is not null
   * This means that the current value is an old state while the new value is being loaded
   */
  get isOld(): boolean {
    return this.loading && this.lazyUpdate && this._lastValue !== null
  }

  /**
   * Get the failed state of the cache entry
   */
  get failed(): boolean {
    return this.cacheHandler.failedCache.has(this.key)
  }

  /**
   * Get the value of the cache entry (sync)
   * If the cache entry is not resolved yet, this will return null
   * If lazyUpdate is enabled, this will return the last resolved value if the cache entry is currently loading
   */
  get value(): T | null {
    if (this.released) {
      console.warn(`Cache entry has been released already - ${this.key}`)
    }

    if (this.loading && this.lazyUpdate && this._lastValue) {
      return this._lastValue
    }

    return this.cacheHandler.get(this.key)
  }

  /**
   * Get the value of the cache entry asynchronously
   * @returns Promise that resolves to the value of the cache entry
   * @throws Error if the cache entry could not be retrieved
   */
  get valueAsync(): Promise<T> {
    if (this.loading && this.lazyUpdate && this._lastValue) {
      return Promise.resolve(this._lastValue)
    }

    return (this.cacheHandler.getAsync(this.key) as Promise<T>).then((val) => {
      if (val !== null) {
        this._lastValue = val
      }
      return val
    })
  }

  /**
   * Try to get the value of the cache entry asynchronously
   * @returns Promise that resolves to the value of the cache entry or null if the cache entry could not be retrieved
   */
  get tryValueAsync(): Promise<T | null> {
    if (this.loading && this.lazyUpdate && this._lastValue) {
      return Promise.resolve(this._lastValue)
    }

    return this.cacheHandler.tryGetAsync(this.key).then((val) => {
      if (val !== null) {
        this._lastValue = val
      }
      return val
    })
  }
}

type ExtensionMethods<Methods> = {
  [K in keyof Methods]: Methods[K] extends (...args: infer Args) => infer R
    ? (...args: Args) => R
    : never;
}

export type CacheEntryExtended<T, Extensions> = CacheEntry<T> & {
  $: Extensions
}
/**
 * CacheHandler is a class that handles caching of a single type
 */
export class CacheHandler<
  T,
  Methods = object,
  Extensions extends ExtensionMethods<Methods> = ExtensionMethods<Methods>,
> extends EasyEventEmitter<{
  afterInvalidate: (
    key: string,
    value: T,
    oldValue: T,
    reload: boolean
  ) => void
  afterRetrieve: (
    key: string,
    value: T,
    oldValue: T,
  ) => void
}> {
  public promiseCache: Map<string, Promise<T | null>> = new Map()
  public cacheResolved: Map<string, T | null> = reactive(new Map())
  public failedCache: Set<string> = reactive(new Set())
  public loadingCache: Set<string> = reactive(new Set())

  /**
   * Operation counter, used to trigger reactivity sparingly
   */
  _operation = ref(0)
  _invalidating = new Set<string>()
  _expirationTimer: Map<string, NodeJS.Timeout> = new Map()
  _usageCounter: Map<string, number> = new Map()
  _invalidateNextTick: Set<string> = new Set()

  _cacheLog(targetKey: string | null, ...args: any[]): void {
    let addStr = ''
    let addStyles: string[] = []

    if (targetKey !== null) {
      addStr = ` %c[%c${targetKey}%c]%c`
      addStyles = ['color:grey', 'color:green', 'color:grey', 'color:white']
    }

    if (CACHE_LOG) {
      console.debug(
        `%c[%c${this.name}%c]%c${addStr}`,
        'color:grey',
        'color:lime; font-weight: bold',
        'color:grey',
        'color:white',
        ...addStyles,
        ...args,
      )

      // provide stack trace
      // console.group('Stack trace')
      // console.trace()
      // console.groupEnd()
    }
  }

  #cacheError(targetKey: string | null, ...args: any[]): void {
    let addStr = ''
    let addStyles: string[] = []

    if (targetKey !== null) {
      addStr = ` %c[%c${targetKey}%c]%c`
      addStyles = ['color:grey', 'color:red', 'color:grey', 'color:white']
    }

    if (CACHE_LOG) {
      console.error(
        `%c[%c${this.name}%c]%c${addStr}`,
        'color:grey',
        'color:lime; font-weight: bold',
        'color:grey',
        'color:white',
        ...addStyles,
        ...args,
      )
    }
  }

  _scheduleInvalidation(key: string): void {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    // Add timer to invalidate cache
    this._cacheLog(key, 'Scheduling invalidation')
    this._expirationTimer.set(
      key,
      setTimeout(() => {
        this._invalidateNextTick.add(key)
        nextTick(() => {
          this._invalidateNextTick.forEach(this.invalidate.bind(this))
          this._invalidateNextTick.clear()
        })
      }, CACHE_TIMEOUT),
    )
  }

  isLoading(key: string): boolean {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    return this.loadingCache.has(key)
  }

  isFailed(key: string): boolean {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    return this.failedCache.has(key)
  }

  exists(key: string): boolean {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    return this.cacheResolved.has(key)
  }

  invalidate(key: string): void {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key

    if (key.endsWith('/*')) {
      [...this.cacheResolved.keys()]
        .filter(k => k.startsWith(key.slice(0, -1)))
        .forEach((k) => {
          this.invalidate(k)
        })
      return
    }

    if (this._invalidating.has(key))
      return
    this._invalidating.add(key)
    const shouldReload = (this._usageCounter.get(key) ?? 0) > 0
    this._cacheLog(key, `Invalidated${shouldReload ? ' (reloading)' : ''}`)

    this.options.beforeInvalidate?.(
      key,
      this.cacheResolved.get(key) as T,
      shouldReload,
    )
    this.promiseCache.delete(key)
    if (!shouldReload)
      this.cacheResolved.delete(key)
    this.failedCache.delete(key)

    this._operation.value++

    if (this._expirationTimer.has(key)) {
      clearTimeout(this._expirationTimer.get(key)!)
    }

    this._expirationTimer.delete(key)

    if (shouldReload) {
      // Refresh cache
      this._cacheLog(key, 'Refreshing cache')
      const valOld = this.cacheResolved.get(key) as T
      void this._retrieveInternal(key).then((val) => {
        this.options?.afterInvalidate?.(key, val as T, valOld, shouldReload)
        this.emit('afterInvalidate', key, val as T, valOld, shouldReload)
        this._invalidating.delete(key)
      }).catch(() => {
        this._invalidating.delete(key)
      })
    }
    else {
      this._usageCounter.delete(key)
      this._invalidating.delete(key)
    }
  }

  /**
   * Use a cache entry reactively
   * @param key Key of the cache entry
   * @param attachGroup Cache group to attach the cache entry to
   * @param skipRetrieve Skip retrieving the cache entry
   * @param lazyLoad Display the last state of the cache entry while it's loading
   * @returns Cache entry
   */
  useReactive(
    key: WatchSource<string>,
    attachGroup: CacheGroup | null = null,
    skipRetrieve = false,
    lazyLoad = false,
  ): Ref<CacheEntryExtended<T, Extensions>> {
    const activeUse: Ref<CacheEntryExtended<T, Extensions>> = ref() as any

    watch(
      key,
      (newKey) => {
        if (activeUse.value) {
          activeUse.value.release()
        }
        activeUse.value = this.use(newKey, attachGroup, skipRetrieve, lazyLoad)
      },
      { immediate: true },
    )

    return activeUse
  }

  /**
   * Use a cache entry
   * @param key Key of the cache entry
   * @param attachGroup Cache group to attach the cache entry to
   * @param skipRetrieve Skip retrieving the cache entry
   * @param lazyUpdate Only update the cache entry when it's being used
   * @returns Cache entry
   */
  use(
    key: string,
    attachGroup: CacheGroup | null = null,
    skipRetrieve = false,
    lazyUpdate = false,
  ): CacheEntryExtended<T, Extensions> {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    void this._retrieveInternal(key, skipRetrieve)
    const e = new CacheEntry(
      this as any,
      key,
      lazyUpdate,
    ) as CacheEntryExtended<T, Extensions>

    if (attachGroup) {
      attachGroup.add(e)
    }

    const extensions = this.options.extensions ?? {}
    const helper = {} as any
    for (const key in extensions) {
      helper[key as keyof typeof e] = (
        extensions[key as keyof typeof extensions] as (...args: any) => void
      ).bind(e) as any
    }
    e.$ = helper

    return e as CacheEntryExtended<T, Extensions>
  }

  /**
   * Call the retriever function and cache the result
   * Usually you don't need to call this manually
   */
  async getAsync(key: string): Promise<T | null> {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    return this._retrieveInternal(key)
  }

  async tryGetAsync(key: string): Promise<T | null> {
    try {
      return await this.getAsync(key)
    }
    catch {
      return null
    }
  }

  /**
   * Get the value of the cache entry synchronously
   * @returns Value of the cache entry or null if the cache entry is not resolved yet
   */
  get(key: string): T | null {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    return this.cacheResolved.get(key) || null
  }

  /**
   * Set the value of the cache entry manually
   */
  set(key: string, value: T): void {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    this.promiseCache.set(key, Promise.resolve(value))
    this.cacheResolved.set(key, value)
    this.failedCache.delete(key)
    this._operation.value++
    this._cacheLog(key, 'Set manually to', value)
  }

  /**
   * Acquire the cache entry (mark it as being used)
   */
  acquire(key: string): void {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    if (this._usageCounter.has(key)) {
      this._usageCounter.set(key, (this._usageCounter.get(key) ?? 0) + 1)
    }
    else {
      this._usageCounter.set(key, 1)
    }

    if (this._expirationTimer.has(key)) {
      clearTimeout(this._expirationTimer.get(key))
      this._expirationTimer.delete(key)
    }
  }

  /**
   * Release the cache entry (mark it as not being used)
   */
  release(key: string): void {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key
    if (this._usageCounter.has(key)) {
      this._usageCounter.set(key, (this._usageCounter.get(key) ?? 0) - 1)
    }
    // this.#cacheLog('Releasing', key, this.usageCounter.get(key))
    if (this._usageCounter.get(key) === 0) {
      this._scheduleInvalidation(key)
    }
  }

  /**
   * Internal function to retrieve the cache entry
   */
  _retrieveInternal(key: string, skipRetrieve = false): Promise<T | null> {
    key = this.options.keyProcessor ? this.options.keyProcessor(key) : key

    if (this.promiseCache.has(key)) {
      return this.promiseCache.get(key) as Promise<T>
    }

    // if (this.cacheResolved.has(key) && !) {
    //   return Promise.resolve(this.cacheResolved.get(key) as T)
    // }

    if (this.failedCache.has(key)) {
      return Promise.resolve(null)
    }

    if (!skipRetrieve) {
      this.loadingCache.add(key)
      this._operation.value++

      this._cacheLog(key, 'Retrieving')

      try {
        const promise = this.retrieve(key)
        const resolve = async (): Promise<T | null> => {
          try {
            const previous = this.cacheResolved.get(key) as T
            const res = await promise
            this.cacheResolved.set(key, res)
            this.failedCache.delete(key)
            this.options?.afterRetrieve?.(key, res as T, previous)
            this.emit('afterRetrieve', key, res as T, previous)
            this._operation.value++
            return res
          }
          catch (e) {
            if (e instanceof ResponseError) {
              if (e.response && e.response.status === 404) {
                this._cacheLog(`${key}`, '404 - Does not exist')
                this.failedCache.add(key)
                this.cacheResolved.delete(key)
              }
              else if (e.response && e.response.status === 429) {
                // Too many request
                await wait(TOO_MANY_REQUEST_TIMEOUT)
                // Retry IF still being used
                if ((this._usageCounter.get(key) ?? 0) > 0) {
                  return await this._retrieveInternal(key, false)
                }
              }
              else {
                this.#cacheError(
                  `Server failed to retrieve ${key}: ${e.response?.status ?? 0}`,
                  { error: e },
                )
                console.trace(`${key}`)
                this.failedCache.add(key)
                this.cacheResolved.delete(key)
              }
            }
            else {
              this.#cacheError(`Couldn't retrieve ${key}`, { error: e })
              this.failedCache.add(key)
              this.cacheResolved.delete(key)
            }
            console.log(key, 1)
            this.loadingCache.delete(key)
            this._operation.value++

            this.options?.afterRetrieve?.(key, null as T, this.cacheResolved.get(key) as T)
            this.emit('afterRetrieve', key, null as T, this.cacheResolved.get(key) as T)
            return null
          }
        }
        this.promiseCache.set(key, resolve().then((val) => {
          this.loadingCache.delete(key)
          this._operation.value++
          return val
        }))

        return promise
      }
      catch (e) {
        console.log(key, 3)
        this.loadingCache.delete(key)
        this._operation.value++

        console.error(`Couldn't retrieve ${key}`, e)
        return Promise.resolve(null)
      }
    }
    console.log(key, 4)
    this.loadingCache.delete(key)
    this._operation.value++

    return Promise.resolve(null)
  }

  // Overrides

  constructor(
    public name: string,
    public retrieve: (key: string) => Promise<T | null>,
    public options: {
      beforeInvalidate?: (key: string, value: T, reload: boolean) => void
      afterInvalidate?: (
        key: string,
        value: T,
        valueOld: T,
        reload: boolean
      ) => void
      afterRetrieve?: (key: string, value: T, valueOld: T) => void
      keyProcessor?: (key: string) => string
      extensions?: Methods
    } = {},
  ) {
    super()
  }
}

// export const StorageDownloadCache = {
//   cache: new Map<string, string | false>(), // path, blob
//   set (path: string, blob: string | false) {
//     this.cache.set(path, blob)
//   },
//   async get (path: string) {
//     if (this.cache.has(path)) {
//       return this.cache.get(path)
//     }

//     const blob = await storageApi.download(path)
//     if (blob) {
//       this.cache.set(path, window.URL.createObjectURL(blob))
//       return this.cache.get(path)
//     }
//     else {
//       this.cache.set(path, false)
//       return false
//     }
//   },
// }
